/**
 * P3.3 — Cookie consent banner.
 *
 * Renders on first visit (when useCookieConsent().needsDecision is true)
 * and whenever the user re-opens via the footer "Cookie preferences"
 * link (which sets bannerOpen via reopenBanner()).
 *
 * Two states:
 *   collapsed: short body + "Accept all" + "Reject all" + "Customize"
 *   expanded:  4-toggle list (essential locked on) + "Save selection"
 *
 * Designed per DESIGN_SYSTEM.md "Neon Dusk" — dark glass surface, coral
 * accent on primary actions, never solid white.
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Switch,
  Link as MuiLink,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import { useCookieConsent } from '../../contexts/CookieConsentContext';
import { colors, designTokens } from '../../theme/theme';

// Token aliases — keep the JSX readable while matching real theme exports.
// Neutral scale is inverted (50 = darkest, 900 = lightest) for dark mode.
const TEXT_PRIMARY = colors.neutral[900];
const TEXT_SECONDARY = colors.neutral[600];
const ACCENT = colors.primary.main;
const ACCENT_DEEP = colors.primary.dark;

export default function CookieBanner() {
  const { t } = useTranslation();
  const {
    consent,
    bannerOpen,
    acceptAll,
    rejectAll,
    saveCustom,
  } = useCookieConsent();

  const [expanded, setExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(consent.analytics);
  const [marketing, setMarketing] = useState(consent.marketing);
  const [preferences, setPreferences] = useState(consent.preferences);

  if (!bannerOpen) return null;

  const handleSaveCustom = () => {
    saveCustom({ analytics, marketing, preferences });
  };

  return (
    <Box
      role="dialog"
      aria-modal="false"
      aria-label={t('cookies.title')}
      sx={{
        position: 'fixed',
        bottom: { xs: 0, md: 24 },
        left: { xs: 0, md: 24 },
        right: { xs: 0, md: 'auto' },
        maxWidth: { xs: '100%', md: 480 },
        zIndex: 2000,
        background: designTokens.glass.surface,
        backdropFilter: designTokens.glass.blur,
        border: `1px solid ${designTokens.glass.border}`,
        borderRadius: { xs: 0, md: `${designTokens.radius.lg}px` },
        padding: { xs: 2.5, md: 3 },
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1.5}>
        <Typography variant="h6" sx={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
          {t('cookies.title')}
        </Typography>
        {/* Close button only useful when user previously consented and re-opens */}
        {consent.decidedAt !== null && (
          <IconButton
            size="small"
            onClick={() => saveCustom({ analytics, marketing, preferences })}
            aria-label="Close"
            sx={{ color: TEXT_SECONDARY, mt: -0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      <Typography variant="body2" sx={{ color: TEXT_SECONDARY, mb: 2 }}>
        {t('cookies.body')}{' '}
        <MuiLink
          href="/legal/privacy"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: ACCENT }}
        >
          {t('cookies.more_info')}
        </MuiLink>
      </Typography>

      {expanded && (
        <Box sx={{ mb: 2 }}>
          <Divider sx={{ borderColor: designTokens.glass.border, mb: 2 }} />

          {/* Essential — always on, disabled toggle for clarity */}
          <ToggleRow
            label={t('cookies.essential.label')}
            desc={t('cookies.essential.desc')}
            checked
            disabled
            onChange={() => {
              /* essential cookies are locked on; the toggle is disabled */
            }}
          />
          <ToggleRow
            label={t('cookies.analytics.label')}
            desc={t('cookies.analytics.desc')}
            checked={analytics}
            onChange={setAnalytics}
          />
          <ToggleRow
            label={t('cookies.marketing.label')}
            desc={t('cookies.marketing.desc')}
            checked={marketing}
            onChange={setMarketing}
          />
          <ToggleRow
            label={t('cookies.preferences.label')}
            desc={t('cookies.preferences.desc')}
            checked={preferences}
            onChange={setPreferences}
          />

          <Divider sx={{ borderColor: designTokens.glass.border, mt: 1, mb: 2 }} />
        </Box>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.25}
        useFlexGap
        flexWrap="wrap"
      >
        {!expanded && (
          <Button
            variant="text"
            size="medium"
            onClick={() => setExpanded(true)}
            sx={{ color: TEXT_SECONDARY, justifyContent: 'flex-start' }}
          >
            {t('cookies.customize')}
          </Button>
        )}
        <Button
          variant="outlined"
          size="medium"
          onClick={rejectAll}
          sx={{
            borderColor: designTokens.glass.border,
            color: TEXT_PRIMARY,
            '&:hover': { borderColor: TEXT_PRIMARY },
          }}
        >
          {t('cookies.reject_all')}
        </Button>
        {expanded ? (
          <Button
            variant="contained"
            size="medium"
            onClick={handleSaveCustom}
            sx={{
              background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DEEP} 100%)`,
              color: '#fff',
              fontWeight: 600,
              '&:hover': { background: ACCENT_DEEP },
            }}
          >
            {t('cookies.save_selection')}
          </Button>
        ) : (
          <Button
            variant="contained"
            size="medium"
            onClick={acceptAll}
            sx={{
              background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DEEP} 100%)`,
              color: '#fff',
              fontWeight: 600,
              '&:hover': { background: ACCENT_DEEP },
            }}
          >
            {t('cookies.accept_all')}
          </Button>
        )}
      </Stack>
    </Box>
  );
}

interface ToggleRowProps {
  label: string;
  desc: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}

function ToggleRow({ label, desc, checked, disabled, onChange }: ToggleRowProps) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ py: 1 }}>
      <Box sx={{ flex: 1, pr: 2 }}>
        <Typography variant="body2" sx={{ color: TEXT_PRIMARY, fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="caption" sx={{ color: TEXT_SECONDARY, lineHeight: 1.5 }}>
          {desc}
        </Typography>
      </Box>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(_, next) => onChange(next)}
        sx={{
          '& .MuiSwitch-thumb': { boxShadow: 'none' },
          '& .MuiSwitch-track': {
            backgroundColor: disabled ? ACCENT : undefined,
            opacity: disabled ? 0.5 : undefined,
          },
        }}
      />
    </Stack>
  );
}
